import { type AskPunttProjectData } from "@mg/schemas/src/christo/askPuntt";
import { BellRinging, Check } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

interface ProjectReviewStatusBannerProps {
  projectData: AskPunttProjectData;
  /**
   * True while the Vitalora killer-flow typewriter is still revealing
   * the parallel review. Forces the banner into the "AI review in
   * progress" yellow state regardless of the stages payload — the
   * payload lands fully resolved (some stages `done`, some
   * `needs_review`) before the user has visibly seen any reviewer
   * run, so showing "Reg needs human review — Notify Reviewers"
   * during the typewriter would give the result away ahead of the
   * theatrical reveal. Cleared automatically when streaming
   * completes; the banner then falls through to the normal stages-
   * driven branches below.
   */
  isKillerFlowStreaming?: boolean;
}

/**
 * Status banner that sits above the chat composer reflecting where the
 * project review is in its lifecycle:
 *
 *   * killer-flow streaming      → yellow "AI review in progress" (forced)
 *   * active stages              → yellow "AI review in progress"
 *   * needs_review + not notified → red "X needs human review — Notify"
 *   * needs_review + notified    → green "Notifications have been sent"
 *
 * The notify state is held locally (resets whenever the underlying needs-
 * review set changes — e.g. a new round flips Regulatory back to
 * needs_review). It was previously implicit on `hasNeedsReview && !allDone`,
 * which made the green stripe lie: it claimed notifications had gone out
 * the moment stages flipped, with no actual action having been taken.
 */
export function ProjectReviewStatusBanner({
  projectData,
  isKillerFlowStreaming,
}: ProjectReviewStatusBannerProps) {
  const stages = projectData.stages ?? [];
  const hasActive = stages.some((s) => s.status === "active");
  const hasNeedsReview = stages.some((s) => s.status === "needs_review");
  const allDone = stages.every((s) => s.status === "done");

  const needsReviewKey = stages
    .filter((s) => s.status === "needs_review")
    .map((s) => s.name ?? "")
    .join("|");

  const [notified, setNotified] = useState(false);
  // Reset the notified flag whenever the needs-review set changes so a new
  // round (e.g. Regulatory re-fires after a revision) re-prompts the user.
  useEffect(() => {
    setNotified(false);
  }, [needsReviewKey]);

  if (stages.length === 0) return null;

  if (hasActive || isKillerFlowStreaming) {
    return (
      <div
        className="rounded-lg border border-puntt-gold-5 bg-puntt-gold-2 px-3 py-2.5"
        data-testid="project-review-banner-active"
        aria-label="AI review in progress"
      >
        <Flex align="start" gap="2">
          <span className="mt-1.5 size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-gold-9" />
          <div>
            <Text size="2" weight="medium" className="text-puntt-gold-11">
              AI review in progress
            </Text>
            <Text as="p" size="1" className="mt-0.5 text-puntt-gold-9">
              AskPuntt will update you on which stages can be skipped (no human
              needed) and which need human review before the project can
              progress.
            </Text>
          </div>
        </Flex>
      </div>
    );
  }

  if (hasNeedsReview && !allDone) {
    if (notified) {
      return (
        <div className="rounded-lg border border-malachite-200 bg-malachite-50 px-3 py-2.5">
          <Flex align="start" gap="2">
            <Check
              size={14}
              weight="bold"
              className="mt-0.5 shrink-0 text-malachite-600"
            />
            <Text size="2" weight="medium" className="text-malachite-800">
              Notifications have been sent to reviewers who need to complete
              their review before the project can progress.
            </Text>
          </Flex>
        </div>
      );
    }

    const needsReviewNames = stages
      .filter((s) => s.status === "needs_review")
      .map((s) => s.name ?? "Stage");
    const reviewerLabel =
      needsReviewNames.length === 1
        ? `${needsReviewNames[0]} needs human review`
        : `${needsReviewNames.join(" & ")} need human review`;

    return (
      <div className="rounded-lg border border-puntt-gold-5 bg-puntt-gold-2 px-3 py-2.5">
        <Flex align="center" gap="3" wrap="wrap">
          <Flex align="start" gap="2" className="min-w-0 flex-1">
            <BellRinging
              size={14}
              weight="fill"
              className="mt-0.5 shrink-0 text-puntt-gold-9"
            />
            <Text size="2" weight="medium" className="text-puntt-gold-11">
              {reviewerLabel} — notify reviewers so the project can progress.
            </Text>
          </Flex>
          <Button
            size="2"
            variant="solid"
            color="amber"
            className="shrink-0 cursor-pointer"
            data-testid="project-review-banner-notify-btn"
            onClick={() => setNotified(true)}
          >
            <BellRinging size={14} weight="fill" />
            Notify Reviewers
          </Button>
        </Flex>
      </div>
    );
  }

  return null;
}
